import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getContactQualityDisplay } from "../helpers/clientDisplayHelper";

const formatMessage = (template, values) =>
  Object.entries(values).reduce(
    (message, [key, value]) => message.replace(`{${key}}`, value),
    template,
  );

const ContactQualityIndicator = ({ value, t }) => {
  const quality = getContactQualityDisplay(value, t);
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const tooltipId = useId();

  const close = () => {
    setIsPinned(false);
    setIsOpen(false);
  };

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current || !tooltipRef.current) return;

    const updatePosition = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const margin = 8;
      const fitsBelow =
        rect.bottom + margin + tooltipRect.height <= window.innerHeight - margin;
      setPosition({
        top: fitsBelow
          ? rect.bottom + margin
          : Math.max(margin, rect.top - tooltipRect.height - margin),
        left: Math.min(
          Math.max(
            margin,
            rect.left + rect.width / 2 - tooltipRect.width / 2,
          ),
          window.innerWidth - tooltipRect.width - margin,
        ),
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    window.visualViewport?.addEventListener("resize", updatePosition);
    window.visualViewport?.addEventListener("scroll", updatePosition);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      window.visualViewport?.removeEventListener("resize", updatePosition);
      window.visualViewport?.removeEventListener("scroll", updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event) => {
      if (
        !triggerRef.current?.contains(event.target) &&
        !tooltipRef.current?.contains(event.target)
      ) {
        close();
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") close();
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const reasonLabel =
    !quality.isKnown && quality.isAvailable
      ? `${quality.reason}. ${t("clientsTable.contactQuality.unknown")}`
      : quality.reason;
  const accessibleLabel = formatMessage(
    t("clientsTable.contactQuality.triggerLabel"),
    {
      severity: quality.severityLabel,
      reason: reasonLabel,
    },
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`contact-quality-trigger contact-quality-${quality.severity}`}
        aria-label={accessibleLabel}
        aria-describedby={isOpen ? tooltipId : undefined}
        aria-expanded={isOpen}
        onClick={(event) => {
          event.stopPropagation();
          const nextPinned = !isPinned;
          setIsPinned(nextPinned);
          setIsOpen(nextPinned);
        }}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => {
          if (!isPinned) setIsOpen(false);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => {
          if (!isPinned) setIsOpen(false);
        }}
      >
        <span className="contact-quality-dot" aria-hidden="true" />
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            className={`contact-quality-tooltip contact-quality-${quality.severity}`}
            style={position}
          >
            <strong>{quality.severityLabel}</strong>
            <span>{quality.reason}</span>
            {!quality.isKnown && quality.isAvailable && (
              <small>{t("clientsTable.contactQuality.unknown")}</small>
            )}
          </div>,
          document.body,
        )}
    </>
  );
};

export default ContactQualityIndicator;
